import type { Random } from '../../core/Random';

export interface MoneyValueEntry {
  /** Coin value as a multiplier of the spin bet. {@code 1} means "1× bet". */
  betMultiplier: number;
  /** Relative weight in the value-pool draw. Higher = more common. */
  weight: number;
}

/**
 * Pluggable strategy for assigning a coin value to a money symbol the
 * moment it lands. Implementations decide the distribution — weighted
 * pool, progressive jackpot, fixed sequence (for tests), etc.
 */
export interface MoneyValueProvider {
  /**
   * Return the coin value for a single money cell, given the spin bet.
   * Called once per landed money symbol (trigger spin or respin).
   */
  pickValue(bet: number, random: Random): number;
}

/**
 * Default value pool — small values dominate, large values are rare. The
 * distribution skews toward "lots of small wins, occasional big one"
 * which matches the MMS hold-and-spin feel. Tuned so the expected
 * payout-per-cell ≈ 4.5× bet.
 *
 * <p>The arithmetic of the bonus is: trigger needs 6 money cells, expected
 * payout per cell ~4.5× bet → trigger floor ≈ 27× bet just from the trigger
 * spin. Respins on top push the EV well into "feels worth chasing" territory.
 */
export const DEFAULT_MONEY_VALUE_POOL: readonly MoneyValueEntry[] = [
  { betMultiplier: 1, weight: 50 },
  { betMultiplier: 2, weight: 30 },
  { betMultiplier: 5, weight: 15 },
  { betMultiplier: 10, weight: 8 },
  { betMultiplier: 25, weight: 4 },
  { betMultiplier: 100, weight: 2 },
  { betMultiplier: 250, weight: 1 },
];

/**
 * Weighted-pool {@link MoneyValueProvider}. Draws values from the pool
 * with frequency proportional to {@link MoneyValueEntry#weight}, multiplied
 * by the spin bet.
 *
 * <p>Pure function on its inputs — pass a deterministic {@link Random}
 * for reproducible tests.
 */
export class WeightedMoneyValueProvider implements MoneyValueProvider {
  private readonly entries: readonly MoneyValueEntry[];
  private readonly totalWeight: number;

  constructor(pool: readonly MoneyValueEntry[] = DEFAULT_MONEY_VALUE_POOL) {
    if (pool.length === 0) {
      throw new Error('WeightedMoneyValueProvider: pool must be non-empty');
    }
    this.entries = pool;
    this.totalWeight = pool.reduce((sum, e) => sum + e.weight, 0);
    if (this.totalWeight <= 0) {
      throw new Error('WeightedMoneyValueProvider: total weight must be > 0');
    }
  }

  pickValue(bet: number, random: Random): number {
    let pick = random.nextFloat() * this.totalWeight;
    for (const entry of this.entries) {
      pick -= entry.weight;
      if (pick <= 0) return Math.round(bet * entry.betMultiplier);
    }
    // Floating-point rounding fallback: return the last entry.
    return Math.round(bet * this.entries[this.entries.length - 1]!.betMultiplier);
  }
}

/**
 * Test fixture — yields fixed values from a sequence, looping back to
 * the start when exhausted. Lets unit tests assert exact bonus payouts
 * without simulating the random draw.
 */
export class FixedMoneyValueProvider implements MoneyValueProvider {
  private cursor = 0;
  constructor(private readonly values: readonly number[]) {
    if (values.length === 0) {
      throw new Error('FixedMoneyValueProvider: values must be non-empty');
    }
  }
  pickValue(_bet: number, _random: Random): number {
    const v = this.values[this.cursor]!;
    this.cursor = (this.cursor + 1) % this.values.length;
    return v;
  }
}
